import { useCallback, useEffect, useMemo, useState } from 'react';

import AdminPageShell from '../AdminPageShell';
import AdminPagination from '../AdminPagination';
import AdminToast from '../AdminToast';
import AdminTableDeleteButton from '../AdminTableDeleteButton';
import AdminConfirmDialog from '../AdminConfirmDialog';
import LucideSymbolIcon from '../../icons/LucideSymbolIcon';
import AcademiaSelect from '../../common/AcademiaSelect';
import { useRegisterPortalRefresh } from '../../../contexts/PortalRefreshContext';
import { ADMIN_TABLE_PAGE_SIZE } from '../../../hooks/useAdminTablePagination';
import {
  emptyAdminCorbeille,
  fetchAdminCorbeille,
  removeAdminCorbeilleItem,
  restoreAdminCorbeilleItem,
} from '../../../services/academiaAdminService';
import { isAdministrateur } from '../../../utils/adminAccess';
import {
  AcademicPanelCard,
  AcademicPanelListItem,
  AcademicPanelTableWrap,
} from './AcademicPanelCard';

const CORBEILLE_CONFIRMATION_PHRASE = 'VIDER CORBEILLE';

type CorbeilleItem = {
  id: string;
  typeEntite: string;
  typeLabel: string;
  idEntite: string;
  libelle: string;
  resume?: string | null;
  restaurable?: boolean;
  supprimePar: string;
  dateSuppression: string;
};

type CorbeilleData = {
  items?: CorbeilleItem[];
  pagination?: { page: number; limit: number; total: number; totalPages: number };
  stats?: { total: number; parType?: Array<{ typeEntite: string; typeLabel: string; count: number }> };
  types?: Array<{ value: string; label: string }>;
};

function formatDateTime(value?: string) {
  if (!value) return '—';
  try {
    return new Intl.DateTimeFormat('fr-FR', {
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(new Date(value));
  } catch {
    return value;
  }
}

export default function AdminCorbeillePanel({ user }: { user?: { role?: string } | null }) {
  const isAdmin = isAdministrateur(user);
  const [data, setData] = useState<CorbeilleData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; variant: 'success' | 'error' } | null>(null);
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [searchDraft, setSearchDraft] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [restoreTarget, setRestoreTarget] = useState<CorbeilleItem | null>(null);
  const [restoreError, setRestoreError] = useState<string | null>(null);
  const [emptyOpen, setEmptyOpen] = useState(false);
  const [emptyPhrase, setEmptyPhrase] = useState('');
  const [emptying, setEmptying] = useState(false);

  const load = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    setError(null);
    try {
      const row = await fetchAdminCorbeille({
        page,
        limit: ADMIN_TABLE_PAGE_SIZE,
        typeEntite: typeFilter || undefined,
        search: search || undefined,
      });
      setData(row as CorbeilleData);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de charger la corbeille');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, page, search, typeFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  useRegisterPortalRefresh(load);

  const items = data?.items ?? [];
  const pagination = data?.pagination;
  const total = pagination?.total ?? 0;

  const typeOptions = useMemo(() => {
    const fromApi = data?.types ?? [];
    return [{ value: '', label: 'Tous les types' }, ...fromApi.map((t) => ({ value: t.value, label: t.label }))];
  }, [data?.types]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchDraft.trim());
  };

  const handleRemove = async (id: string) => {
    setDeletingId(id);
    try {
      await removeAdminCorbeilleItem(id);
      setToast({ message: 'Élément retiré de la corbeille.', variant: 'success' });
      await load();
    } catch (e) {
      setToast({
        message: e instanceof Error ? e.message : 'Suppression impossible.',
        variant: 'error',
      });
      throw e;
    } finally {
      setDeletingId(null);
    }
  };

  const handleRestore = async () => {
    if (!restoreTarget) return;
    setRestoringId(restoreTarget.id);
    setRestoreError(null);
    try {
      const response = await restoreAdminCorbeilleItem(restoreTarget.id);
      setToast({
        message: response?.message || `« ${restoreTarget.libelle} » a été restauré.`,
        variant: 'success',
      });
      setRestoreTarget(null);
      await load();
    } catch (e) {
      setRestoreError(e instanceof Error ? e.message : 'Restauration impossible.');
    } finally {
      setRestoringId(null);
    }
  };

  const handleEmpty = async () => {
    setEmptying(true);
    try {
      const response = await emptyAdminCorbeille(emptyPhrase);
      setToast({
        message: response?.message || 'Corbeille vidée.',
        variant: 'success',
      });
      setEmptyOpen(false);
      setEmptyPhrase('');
      setPage(1);
      await load();
    } catch (e) {
      setToast({
        message: e instanceof Error ? e.message : 'Impossible de vider la corbeille.',
        variant: 'error',
      });
    } finally {
      setEmptying(false);
    }
  };

  if (!isAdmin) {
    return (
      <AdminPageShell title="Corbeille" description="Réservé à l’administrateur.">
        <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          Accès refusé — cette section est réservée à l’administrateur.
        </p>
      </AdminPageShell>
    );
  }

  return (
    <AdminPageShell
      title="Corbeille"
      description="Historique des éléments supprimés par l’administration. Les suppressions récentes peuvent être restaurées ; les archives sans snapshot ne sont consultables qu’à titre de traçabilité."
      sideCard={{
        label: 'Éléments archivés',
        value: loading ? '…' : String(total),
        helper: 'Restauration disponible lorsque l’archive contient un snapshot.',
      }}
    >
      {toast ? (
        <AdminToast message={toast.message} variant={toast.variant} onClose={() => setToast(null)} />
      ) : null}

      <div className="space-y-6">
        <AcademicPanelCard
          icon={<LucideSymbolIcon name="delete_forever" tone="primary" size={22} />}
          title="Filtres"
          subtitle="Recherche par libellé, identifiant ou type d’entité."
        >
          <form className="flex flex-col gap-3 sm:flex-row sm:items-end" onSubmit={handleSearch}>
            <label className="block min-w-0 flex-1">
              <span className="text-xs font-semibold text-on-surface-variant">Recherche</span>
              <input
                type="search"
                className="mt-1 w-full rounded-xl border border-outline-variant/30 bg-white px-3 py-2 text-sm"
                placeholder="Libellé, matricule, code…"
                value={searchDraft}
                onChange={(e) => setSearchDraft(e.target.value)}
              />
            </label>
            <label className="block w-full sm:w-56">
              <span className="text-xs font-semibold text-on-surface-variant">Type</span>
              <AcademiaSelect
                className="mt-1"
                value={typeFilter}
                onChange={(e) => {
                  setTypeFilter(e.target.value);
                  setPage(1);
                }}
              >
                {typeOptions.map((opt) => (
                  <option key={opt.value || 'all'} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </AcademiaSelect>
            </label>
            <button
              type="submit"
              className="rounded-xl bg-primary px-4 py-2 text-sm font-bold text-white"
            >
              Rechercher
            </button>
          </form>
        </AcademicPanelCard>

        <AcademicPanelCard
          flush
          icon={<LucideSymbolIcon name="inventory_2" tone="primary" size={22} />}
          title="Éléments supprimés"
          subtitle={
            loading
              ? 'Chargement…'
              : `${total} élément${total !== 1 ? 's' : ''} dans la corbeille`
          }
          footer={
            total > 0 ? (
              <button
                type="button"
                className="academic-panel-btn academic-panel-btn--danger"
                onClick={() => {
                  setEmptyPhrase('');
                  setEmptyOpen(true);
                }}
              >
                <LucideSymbolIcon name="delete_forever" tone="inherit" size={16} />
                Vider la corbeille
              </button>
            ) : null
          }
        >
          {error ? (
            <p className="mx-4 mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</p>
          ) : null}

          <AcademicPanelTableWrap
            footer={
              <AdminPagination
                className="px-4 py-3"
                currentPage={pagination?.page ?? page}
                totalPages={pagination?.totalPages ?? 1}
                onPageChange={setPage}
                summary={
                  loading
                    ? 'Chargement…'
                    : `${total} élément${total !== 1 ? 's' : ''}`
                }
              />
            }
          >
            {!loading && items.length === 0 ? (
              <p className="px-4 py-10 text-center text-sm text-on-surface-variant">
                La corbeille est vide. Les prochaines suppressions admin y seront archivées.
              </p>
            ) : (
              <ul className="divide-y divide-outline-variant/10">
                {items.map((row) => (
                  <AcademicPanelListItem key={row.id}>
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-bold uppercase text-slate-700">
                            {row.typeLabel}
                          </span>
                          <p className="truncate text-sm font-bold text-slate-900">{row.libelle}</p>
                        </div>
                        {row.resume ? (
                          <p className="mt-1 text-xs text-on-surface-variant">{row.resume}</p>
                        ) : null}
                        <p className="mt-2 font-mono text-[11px] text-on-surface-variant">ID : {row.idEntite}</p>
                        <p className="mt-1 text-xs text-on-surface-variant">
                          Supprimé le {formatDateTime(row.dateSuppression)} par {row.supprimePar}
                        </p>
                      </div>
                      <div className="flex shrink-0 flex-wrap items-center gap-2">
                        {row.restaurable ? (
                          <button
                            type="button"
                            disabled={restoringId === row.id || deletingId === row.id}
                            className="academic-panel-btn !inline-flex !px-2.5 !py-1.5 text-xs !gap-1.5 whitespace-nowrap disabled:opacity-50"
                            onClick={() => {
                              setRestoreError(null);
                              setRestoreTarget(row);
                            }}
                          >
                            <LucideSymbolIcon name="refresh" tone="inherit" size={14} />
                            Restaurer
                          </button>
                        ) : (
                          <span
                            className="rounded-lg border border-outline-variant/20 px-2.5 py-1.5 text-[11px] text-on-surface-variant"
                            title="Archive sans snapshot — restauration indisponible"
                          >
                            Non restaurable
                          </span>
                        )}
                        <AdminTableDeleteButton
                          canDelete
                          label="Retirer"
                          confirmTitle="Retirer de la corbeille"
                          confirmMessage={`Retirer définitivement l’archive « ${row.libelle} » de la corbeille ? L’élément reste supprimé de la plateforme.`}
                          disabled={deletingId === row.id || restoringId === row.id}
                          onDelete={() => handleRemove(row.id)}
                        />
                      </div>
                    </div>
                  </AcademicPanelListItem>
                ))}
              </ul>
            )}
          </AcademicPanelTableWrap>
        </AcademicPanelCard>
      </div>

      {emptyOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 p-4">
          <div className="w-full max-w-md rounded-2xl border border-outline-variant/20 bg-white p-6 shadow-2xl">
            <h3 className="text-lg font-black text-slate-900">Vider la corbeille</h3>
            <p className="mt-2 text-sm text-on-surface-variant">
              Tous les éléments archivés seront retirés définitivement de la corbeille. Les données supprimées de la
              plateforme ne seront pas restaurées.
            </p>
            <p className="mt-3 text-sm font-semibold text-slate-800">
              Saisissez <span className="font-mono text-primary">{CORBEILLE_CONFIRMATION_PHRASE}</span> pour confirmer.
            </p>
            <input
              type="text"
              className="mt-2 w-full rounded-xl border border-outline-variant/30 px-3 py-2 text-sm"
              value={emptyPhrase}
              onChange={(e) => setEmptyPhrase(e.target.value)}
              autoComplete="off"
            />
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-xl border px-4 py-2 text-sm font-semibold text-on-surface-variant"
                disabled={emptying}
                onClick={() => setEmptyOpen(false)}
              >
                Annuler
              </button>
              <button
                type="button"
                className="rounded-xl bg-red-600 px-4 py-2 text-sm font-bold text-white disabled:opacity-50"
                disabled={emptying || emptyPhrase.trim() !== CORBEILLE_CONFIRMATION_PHRASE}
                onClick={() => void handleEmpty()}
              >
                {emptying ? 'Suppression…' : 'Vider'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <AdminConfirmDialog
        open={restoreTarget != null}
        title="Restaurer l’élément"
        description={
          restoreTarget
            ? `Réintégrer « ${restoreTarget.libelle} » (${restoreTarget.typeLabel}) dans la plateforme ? L’archive sera retirée de la corbeille après restauration.`
            : null
        }
        confirmLabel="Restaurer"
        variant="neutral"
        isConfirming={restoringId != null}
        errorMessage={restoreError}
        onCancel={() => {
          if (restoringId == null) {
            setRestoreTarget(null);
            setRestoreError(null);
          }
        }}
        onConfirm={() => void handleRestore()}
      />
    </AdminPageShell>
  );
}
